import { IApiError } from "../@types/ApiError";

/**
 * Error subclass carrying the HTTP status that produced it. The shared axios
 * interceptor in `services/api.ts` returns non-2xx responses as if successful
 * (it doesn't reject); consumers throw `HttpError` from their result-handling
 * code so callers can branch on status (e.g. show a 403-specific UI).
 */
export class HttpError extends Error {
  readonly status: number;

  /**
   * The server's own `ErrorResponse.errors`, when the body carried any. Kept
   * alongside the message so a caller can branch on `code` — a rejection the
   * user can act on ("this type is still used by a workflow") deserves
   * different treatment from a generic failure.
   */
  readonly errors: IApiError[];

  constructor(status: number, message?: string, errors: IApiError[] = []) {
    super(message ?? `HTTP ${status}`);
    // Required because tsconfig targets ES5: extending a built-in there
    // leaves the instance prototyped as Error, so `instanceof HttpError`
    // (and `isForbidden`) would always be false.
    Object.setPrototypeOf(this, HttpError.prototype);
    this.name = "HttpError";
    this.status = status;
    this.errors = errors;
  }
}

export function isForbidden(error: unknown): boolean {
  return error instanceof HttpError && error.status === 403;
}

/** True when the failure carries the given server error code. */
export function hasErrorCode(error: unknown, code: string): boolean {
  return (
    error instanceof HttpError && error.errors.some((e) => e.code === code)
  );
}

/**
 * The server's explanation for a failure, if it sent one. Prefer this over a
 * hard-coded "something went wrong": these endpoints reject with a message
 * written for the person reading it.
 */
export function serverErrorMessage(error: unknown): string | undefined {
  if (!(error instanceof HttpError)) return undefined;
  return error.errors.find((e) => e.message)?.message;
}

/**
 * Toast text for a failed admin request: the server's own messages when the
 * response body carried any, otherwise the caller's fallback with the HTTP
 * status appended. The admin portal is internal-only, so the real reason —
 * however unpolished — beats a hand-written generic one.
 */
export function errorToastMessage(error: unknown, fallback: string): string {
  if (!(error instanceof HttpError)) return fallback;
  const detail = Array.from(
    new Set(error.errors.map((e) => e.message).filter(Boolean))
  ).join(" ");
  return detail || `${fallback} (status ${error.status})`;
}
